import { useEffect, useMemo, useState } from 'react';
import type { PermissionState } from '@capacitor/core';
import {
  leagueShortCode,
  resolveUserSettings,
  type NotificationSettings
} from '@h2h/shared';
import { useAuth } from '../../context/AuthContext';
import {
  useProfile,
  useMyMemberships,
  useLeagues,
  useUpdateUserDoc,
  useDeleteAccount,
  useCompetitionStandings,
  useBlockedUsers,
  useUnblockUser
} from '../../hooks/useQueries';
import { useHaptics } from '../../hooks/useHaptics';
import { currentPushPermission, isPushSupported, requestPushPermission } from '../../lib/push';
import { identityFor, FOCUSED_LEAGUE_ID } from '../../data/leagues';
import { teamCode } from '../../lib/teams';
import { FALLBACK_TEAM_OPTIONS } from './constants';
import type { JoinedLeagueOption, TeamOption } from './types';

export function useSettingsScreen() {
  const { impact } = useHaptics();
  const { uid, user, sendPasswordReset, signOut } = useAuth();
  const { data: profile } = useProfile(uid);
  const { data: memberships } = useMyMemberships(uid);
  const { data: leagues } = useLeagues();
  const updateDoc = useUpdateUserDoc(uid);
  const deleteAccount = useDeleteAccount();
  const { data: blocked } = useBlockedUsers(uid);
  const unblockUser = useUnblockUser(uid);

  const [resetState, setResetState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [picker, setPicker] = useState<'team' | 'league' | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showBlocked, setShowBlocked] = useState(false);

  const pushSupported = isPushSupported();
  const [pushPerm, setPushPerm] = useState<PermissionState | null>(null);
  useEffect(() => {
    if (pushSupported) void currentPushPermission().then(setPushPerm);
  }, [pushSupported]);

  async function onEnablePush() {
    impact('selection');
    setPushPerm(await requestPushPermission());
  }

  const name = profile?.displayName ?? user?.displayName ?? 'Player';
  const username = profile?.username ? `@${profile.username}` : '';
  const tone = profile?.avatarTone ?? 0;

  const settings = useMemo(() => resolveUserSettings(profile?.settings), [profile?.settings]);
  const notifs = settings.notifications;

  const { data: focusStandings } = useCompetitionStandings(FOCUSED_LEAGUE_ID);
  const teamOptions = useMemo<TeamOption[]>(() => {
    const seen = new Map<string, TeamOption>();
    for (const g of focusStandings?.groups ?? []) {
      for (const row of g.rows) {
        if (row.teamName && !seen.has(row.teamName)) {
          seen.set(row.teamName, { name: row.teamName, code: teamCode(row.teamName) });
        }
      }
    }
    const live = [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
    return live.length > 0 ? live : FALLBACK_TEAM_OPTIONS;
  }, [focusStandings]);

  const headlineTier = useMemo(() => {
    const ms = memberships ?? [];
    if (ms.length === 0) return null;
    return ms.reduce((best, m) => Math.max(best, m.tier), 0);
  }, [memberships]);

  const joinedLeagues = useMemo<JoinedLeagueOption[]>(
    () =>
      (memberships ?? []).map((m) => {
        const id = identityFor(m.leagueId);
        const league = leagues?.find((l) => l.leagueId === m.leagueId);
        return {
          leagueId: m.leagueId,
          name: league?.name ?? id?.name ?? m.leagueId,
          country: league?.country ?? id?.country ?? null,
          code: league?.shortCode ?? id?.shortCode ?? leagueShortCode(league?.name ?? id?.name ?? m.leagueId)
        };
      }),
    [memberships, leagues]
  );

  const defaultLeague = joinedLeagues.find((l) => l.leagueId === profile?.defaultLeagueId);
  const favourite = profile?.favouriteTeam;
  const isPasswordUser = user?.providerData.some((p) => p.providerId === 'password') ?? false;

  async function onResetPassword() {
    if (!user?.email || resetState !== 'idle') return;
    setResetState('sending');
    try {
      await sendPasswordReset(user.email);
      setResetState('sent');
    } catch {
      setResetState('idle');
    }
  }

  function setNotif(key: keyof NotificationSettings, value: boolean) {
    impact('selection');
    updateDoc.mutate({ settings: { ...settings, notifications: { ...notifs, [key]: value } } });
  }

  async function onDelete() {
    try {
      await deleteAccount.mutateAsync();
    } finally {
      await signOut();
    }
  }

  return {
    user,
    profile,
    blocked,
    name,
    username,
    tone,
    headlineTier,
    favourite,
    defaultLeague,
    joinedLeagues,
    teamOptions,
    notifs,
    resetState,
    picker,
    confirmDelete,
    showBlocked,
    pushSupported,
    pushPerm,
    isPasswordUser,
    updateDoc,
    deleteAccount,
    unblockUser,
    signOut,
    setPicker,
    setConfirmDelete,
    setShowBlocked,
    onEnablePush,
    onResetPassword,
    setNotif,
    onDelete
  };
}

export type SettingsScreenState = ReturnType<typeof useSettingsScreen>;
